import { readonly, ref } from 'vue'
import { postToDevice } from './scanSource/deviceBridge'
import { appTakesKeepScreenOn, tabletInfoNow, type TabletInfo } from './scanSource/tabletInfo'

/**
 * Keep the Screen Awake (Mark, 2026-10-16, Settings): on, the tablet's screen does not go dark
 * while this site is showing.
 *
 * Through the browser's Screen Wake Lock (`navigator.wakeLock`), which the tablets' WebView
 * has. Tablet Browser also holds the screen on by itself, by the MDM's `keep_screen_on`
 * setting (on while powered, by default); this is the driver's own switch on top of that, so
 * off means "as the tablet is set", never "make it sleep". A wake lock is let go by the
 * browser whenever the page is hidden, so it is taken again each time the page comes back
 * (plugins/keep-awake.client.ts).
 *
 * Tablet Browser 2.21 also takes the switch itself, as the `keep-screen-on` command (on or off;
 * docs/tablets.md): the app then holds the screen on while the page asks, on battery as well,
 * under its own MDM setting (Never still sleeps; Always stays on whatever the page says). The
 * page tells it on every change and whenever the app answers `capabilities` (a restarted app
 * starts with no wish). An older app is never sent the command, and the wake lock carries on
 * alone there, as before.
 *
 * A per-tablet choice kept in this browser; off unless it was turned on here.
 */

const KEEP_AWAKE_KEY = 'dockautomation.keepAwake'

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
type Sentinel = { release: () => Promise<void>, addEventListener?: (type: 'release', listener: () => void) => void }
type WakeLockApi = { request: (type: 'screen') => Promise<Sentinel> }

function storage(): Store | null {
  try {
    return globalThis.localStorage ?? null
  } catch {
    return null
  }
}

function wakeLockApi(): WakeLockApi | null {
  const api = (globalThis.navigator as { wakeLock?: WakeLockApi } | undefined)?.wakeLock
  return api && typeof api.request === 'function' ? api : null
}

export function keepAwakeWanted(store: Store | null = storage()): boolean {
  try {
    return store?.getItem(KEEP_AWAKE_KEY) === 'on'
  } catch {
    return false
  }
}

function rememberKeepAwake(on: boolean, store: Store | null = storage()): void {
  try {
    if (on) store?.setItem(KEEP_AWAKE_KEY, 'on')
    else store?.removeItem(KEEP_AWAKE_KEY)
  } catch {
    // No storage: the choice lasts until the next page load.
  }
}

/** Whether this browser can hold the screen on at all: by a wake lock, or by the tablet app. */
export function keepAwakeSupported(tablet: TabletInfo = tabletInfoNow()): boolean {
  return wakeLockApi() !== null || appTakesKeepScreenOn(tablet)
}

/**
 * Tell Tablet Browser the driver's choice, where the app takes it (2.21). False when it was not
 * told: no app, or an app too old to know the command.
 */
export function tellAppKeepScreenOn(tablet: TabletInfo = tabletInfoNow()): boolean {
  if (!appTakesKeepScreenOn(tablet)) return false
  return postToDevice({ command: 'keep-screen-on', value: keepAwakeWanted() ? 'on' : 'off' })
}

const wanted = ref(false)
const holding = ref(false)
let sentinel: Sentinel | null = null

/** Take the lock if it is wanted and not held; let it go if it is held and not wanted. */
export async function applyKeepAwake(): Promise<void> {
  wanted.value = keepAwakeWanted()
  const api = wakeLockApi()
  if (!api) return
  if (wanted.value && !sentinel && globalThis.document?.visibilityState !== 'hidden') {
    try {
      const taken = await api.request('screen')
      sentinel = taken
      holding.value = true
      taken.addEventListener?.('release', () => {
        if (sentinel === taken) sentinel = null
        holding.value = false
      })
    } catch {
      // Refused (no user gesture yet, battery saver): the next return to the page tries again.
      holding.value = false
    }
  } else if (!wanted.value && sentinel) {
    const held = sentinel
    sentinel = null
    holding.value = false
    await held.release().catch(() => {})
  }
}

/** The Settings switch: remember the choice and act on it now. */
export async function setKeepAwake(on: boolean): Promise<void> {
  rememberKeepAwake(on)
  tellAppKeepScreenOn()
  await applyKeepAwake()
}

export function useKeepAwake() {
  wanted.value = keepAwakeWanted()
  return { wanted: readonly(wanted), holding: readonly(holding) }
}
